/**
 * OverlayOrchestratorContext
 *
 * React context + provider that exposes the overlay orchestrator to the
 * component tree. Subscribes to overlay lifecycle events and triggers
 * React re-renders when the stack changes.
 *
 * Mount this provider once near the app root (inside Router, outside pages).
 *
 * @module lib/navigation/OverlayOrchestratorContext
 */

import React, {
    useState,
    useEffect,
    useLayoutEffect,
    useCallback,
    useMemo,
    useRef,
    type ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type {
    SurfaceId,
    OverlayEntry,
    NavigationContext,
} from './types';
import { surfaceKey } from './types';
import * as orchestrator from './overlayOrchestrator';
import { restoreFocusFromOrigin, clearAllSourceHighlights } from './focusRestoration';
import { resolveHostRoute } from './canonicalHostRegistry';
import { emitTelemetry } from './telemetry';
import { OverlayOrchestratorCtx } from './overlayOrchestratorCtx';
import type { OverlayOrchestratorAPI } from './overlayOrchestratorCtx';
import { useOverlayUrlSync } from './useOverlayUrlSync';

export type { OverlayOrchestratorAPI };

// ── Provider ──────────────────────────────────────────────────────────────

export function OverlayOrchestratorProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [stack, setStack] = useState<readonly OverlayEntry[]>(orchestrator.getStack());

    // Keep React state in sync with orchestrator
    useEffect(() => {
        return orchestrator.subscribe(() => {
            setStack(orchestrator.getStack());
        });
    }, []);

    // Two-way URL ↔ stack sync (deep-link restore + canonical serialization).
    // Mounted here so the hook lives inside the same Provider that owns
    // the overlay state — single source of truth.
    useOverlayUrlSync(stack);

    // Clear the previous route before destination passive effects hydrate a
    // deep-linked drawer. A passive cleanup here would close the NEW drawer.
    const prevPathnameRef = React.useRef(location.pathname);
    useLayoutEffect(() => {
        if (location.pathname !== prevPathnameRef.current) {
            orchestrator.closeAll();
            clearAllSourceHighlights();
            prevPathnameRef.current = location.pathname;
        }
    }, [location.pathname]);

    // ── History integration: back-button closes the topmost overlay ──────
    //
    // Each time an overlay opens we push a sentinel history entry whose
    // state object carries an `__overlayDepth` counter. When the user
    // presses the browser Back button, popstate fires and we observe a
    // shrinking depth — that's the cue to close the top overlay (instead
    // of letting the browser navigate back to the previous route).
    //
    // This makes overlays feel native: Esc, the X button, and the
    // back-button all behave the same way.
    const overlayDepthRef = useRef(0);
    const navigatingFromOverlayRef = useRef(false);

    useEffect(() => {
        const visibleStack = stack.filter(e => e.lifecycle !== 'closed');
        const targetDepth = visibleStack.length;
        const currentDepth = overlayDepthRef.current;

        if (targetDepth > currentDepth) {
            // Push one sentinel per newly-opened overlay so Back pops them
            // one at a time.
            for (let i = currentDepth; i < targetDepth; i++) {
                window.history.pushState(
                    { __overlayDepth: i + 1 },
                    '',
                    window.location.href,
                );
            }
            overlayDepthRef.current = targetDepth;
        } else if (targetDepth < currentDepth && !navigatingFromOverlayRef.current) {
            // Overlays were closed programmatically (X button, ESC, code).
            // We deliberately do NOT call history.go(-n) here — that would
            // race with any programmatic navigate() called immediately after
            // close (e.g. "close drawer then navigate to docs page"). The
            // queued popstate would land *after* the navigate and pop it.
            //
            // Instead, clear the depth marker on the current history entry
            // so the URL state stays clean. The price: a closed overlay
            // leaves stale sentinel entries earlier in history, so the
            // browser Back button may need an extra press to leave the
            // page. Acceptable trade-off for navigation correctness.
            try {
                window.history.replaceState(
                    targetDepth > 0 ? { __overlayDepth: targetDepth } : {},
                    '',
                    window.location.href,
                );
            } catch {
                /* replaceState can throw in cross-origin sandboxes — ignore */
            }
            overlayDepthRef.current = targetDepth;
        }
    }, [stack]);

    useEffect(() => {
        const onPopState = (event: PopStateEvent) => {
            const newDepth = (event.state && typeof event.state === 'object'
                && '__overlayDepth' in event.state)
                ? (event.state as { __overlayDepth: number }).__overlayDepth
                : 0;
            const currentDepth = overlayDepthRef.current;

            if (newDepth < currentDepth && !navigatingFromOverlayRef.current) {
                // Browser back button — close the top overlay(s) to match.
                navigatingFromOverlayRef.current = true;
                const visibleStack = orchestrator.getStack().filter(e => e.lifecycle !== 'closed');
                const overlaysToClose = currentDepth - newDepth;
                for (let i = 0; i < overlaysToClose && visibleStack.length - i > 0; i++) {
                    const top = visibleStack[visibleStack.length - 1 - i];
                    if (top) orchestrator.closeOverlay(top.instanceId, undefined, { immediate: true });
                }
                clearAllSourceHighlights();
                overlayDepthRef.current = newDepth;
                setTimeout(() => { navigatingFromOverlayRef.current = false; }, 0);
            }
        };
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    const open = useCallback<OverlayOrchestratorAPI['open']>((params) => {
        return orchestrator.openOverlay(params);
    }, []);

    const close = useCallback<OverlayOrchestratorAPI['close']>(
        (instanceId, returnPayload) => {
            const entry = orchestrator.closeOverlay(instanceId, returnPayload);
            if (!entry) return;

            const returnPolicy = orchestrator.resolveReturnPolicy(entry);

            emitTelemetry('return-policy-resolved', {
                surface: entry.surface.name,
                returnPolicy,
                hasTrigger: !!entry.origin.triggerElementId,
                hasCanonicalFallback: !!entry.origin.canonicalFallback,
            });

            switch (returnPolicy) {
                case 'close-to-parent': {
                    // Just restore focus — parent overlay is still on the stack
                    if (entry.origin.triggerElementId) {
                        restoreFocusFromOrigin(entry.origin, { keyboardFocus: true });
                    }
                    break;
                }

                case 'redirect-canonical': {
                    // Navigate to the canonical host route
                    const fallback = entry.origin.canonicalFallback;
                    if (fallback) {
                        emitTelemetry('fallback-canonical-used', {
                            surface: entry.surface.name,
                            route: fallback.route,
                        });
                        let route = fallback.route;
                        if (fallback.params) {
                            for (const [param, value] of Object.entries(fallback.params)) {
                                route = route.replace(`:${param}`, encodeURIComponent(value));
                            }
                        }
                        // Guard: if the route still contains unreplaced :params
                        // (e.g. :projectId wasn't in fallback.params because the
                        // origin didn't know the project), the canonical fallback
                        // is not usable. Don't navigate to a literal `:projectId`
                        // URL — fall back to focus-only restoration instead.
                        if (/\/:[a-zA-Z]/.test(route)) {
                            if (entry.origin.triggerElementId) {
                                restoreFocusFromOrigin(entry.origin, { keyboardFocus: true });
                            }
                            break;
                        }
                        navigate(route, { replace: true });
                        // Highlight after navigation settles
                        if (entry.origin.triggerElementId) {
                            setTimeout(() => restoreFocusFromOrigin(entry.origin), 200);
                        }
                    }
                    break;
                }

                case 'restore-origin':
                default: {
                    // Parent page is beneath — highlight the trigger
                    if (entry.origin.triggerElementId) {
                        const parentStillInStack = orchestrator.getStack().some(
                            (e) => surfaceKey(e.surface) === surfaceKey(entry.origin.origin),
                        );

                        // Count only entries that are NOT the one currently closing.
                        // closeOverlay() leaves the entry in the stack with lifecycle
                        // 'closing' until the exit animation completes, so a naive
                        // `stack.length === 0` check is never true at this point and
                        // would incorrectly trigger a canonical-host navigation when
                        // the only overlay was just closed by user action (e.g. X click).
                        const otherEntriesPresent = orchestrator
                            .getStack()
                            .some((e) => e.instanceId !== entry.instanceId && e.lifecycle !== 'closed');

                        if (parentStillInStack || !otherEntriesPresent) {
                            restoreFocusFromOrigin(entry.origin, { keyboardFocus: true });
                        } else {
                            // Parent is gone AND another overlay is still mounted —
                            // try the canonical host route as a last-resort fallback.
                            const hostRoute = resolveHostRoute(
                                entry.surface.kind,
                                entry.surface.name,
                                entry.surface.entityId
                                    ? { projectId: entry.surface.entityId }
                                    : undefined,
                            );
                            if (hostRoute) {
                                navigate(hostRoute, { replace: true });
                                setTimeout(() => restoreFocusFromOrigin(entry.origin), 200);
                            }
                        }
                    }
                    break;
                }
            }
        },
        [navigate],
    );

    const closeAllFn = useCallback(() => {
        orchestrator.closeAll();
        clearAllSourceHighlights();
    }, []);

    const closeAllAboveFn = useCallback((instanceId: string) => {
        const removed = orchestrator.closeAllAbove(instanceId);
        if (removed.length === 0) return;

        // The root overlay is the one that was explicitly requested to close.
        // It is the first element in the removed array (lowest in the stack).
        // Only the root gets full restoration; children were closed silently.
        const root = removed[0];
        const returnPolicy = orchestrator.resolveReturnPolicy(root);

        emitTelemetry('return-policy-resolved', {
            surface: root.surface.name,
            returnPolicy,
            hasTrigger: !!root.origin.triggerElementId,
            hasCanonicalFallback: !!root.origin.canonicalFallback,
            cascade: true,
            cascadedCount: removed.length - 1,
        });

        // Resolve restoration for the root entry only
        if (returnPolicy === 'redirect-canonical') {
            const fallback = root.origin.canonicalFallback;
            if (fallback) {
                emitTelemetry('fallback-canonical-used', {
                    surface: root.surface.name,
                    route: fallback.route,
                });
                let route = fallback.route;
                if (fallback.params) {
                    for (const [param, value] of Object.entries(fallback.params)) {
                        route = route.replace(`:${param}`, encodeURIComponent(value));
                    }
                }
                // Guard: skip navigation if :params weren't all replaced.
                if (/\/:[a-zA-Z]/.test(route)) {
                    if (root.origin.triggerElementId) {
                        restoreFocusFromOrigin(root.origin, { keyboardFocus: true });
                    }
                } else {
                    navigate(route, { replace: true });
                    if (root.origin.triggerElementId) {
                        setTimeout(() => restoreFocusFromOrigin(root.origin), 200);
                    }
                }
            }
        } else if (root.origin.triggerElementId) {
            restoreFocusFromOrigin(root.origin, { keyboardFocus: true });
        }
    }, [navigate]);

    const isOpenFn = useCallback((surface: SurfaceId) => {
        return orchestrator.isOpen(surface);
    }, []);

    const buildOrigin = useCallback<OverlayOrchestratorAPI['buildOrigin']>(
        (originSurface, triggerElementId, extra) => {
            const context: NavigationContext = {
                surface: originSurface,
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
                capturedAt: Date.now(),
                ...extra,
            };
            return orchestrator.buildOriginEnvelope(
                originSurface,
                context,
                triggerElementId,
            );
        },
        [location.pathname, location.search, location.hash],
    );

    const topOverlay = stack.length > 0 ? stack[stack.length - 1] : null;

    const api = useMemo<OverlayOrchestratorAPI>(
        () => ({ stack, open, close, closeAllAbove: closeAllAboveFn, closeAll: closeAllFn, isOpen: isOpenFn, topOverlay, buildOrigin }),
        [stack, open, close, closeAllAboveFn, closeAllFn, isOpenFn, topOverlay, buildOrigin],
    );

    return (
        <OverlayOrchestratorCtx.Provider value={api}>
            {children}
        </OverlayOrchestratorCtx.Provider>
    );
}
